import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    ArrowLeft,
    Plus,
    Edit2,
    Trash2,
    Clock,
    Settings,
    Save,
    X,
    AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
    { value: 'Operations', label: 'Operations', color: 'bg-orange-500' },
    { value: 'Training', label: 'Training', color: 'bg-blue-500' },
    { value: 'Other', label: 'Other', color: 'bg-gray-500' },
];

export default function ManageActivitiesPage() {
    const navigate = useNavigate();
    const { isNetworkManager } = useAuth();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [formData, setFormData] = useState({ name: '', category: 'Operations' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchActivities();
    }, []);

    const fetchActivities = async () => {
        try {
            const response = await axios.get(`${API_URL}/clock-on-activities`);
            setActivities(response.data);
        } catch (error) {
            toast.error('Failed to load activities');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!formData.name.trim()) {
            toast.error('Activity name is required');
            return;
        }
        
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/clock-on-activities`, formData);
            toast.success('Activity created');
            setShowAddDialog(false);
            setFormData({ name: '', category: 'Operations' });
            fetchActivities();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create activity');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async () => {
        if (!formData.name.trim()) {
            toast.error('Activity name is required');
            return;
        }
        
        setSubmitting(true);
        try {
            await axios.put(`${API_URL}/clock-on-activities/${selectedActivity.id}`, formData);
            toast.success('Activity updated');
            setShowEditDialog(false);
            setSelectedActivity(null);
            setFormData({ name: '', category: 'Operations' });
            fetchActivities();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update activity');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        try {
            await axios.delete(`${API_URL}/clock-on-activities/${selectedActivity.id}`);
            toast.success('Activity deleted');
            setShowDeleteDialog(false);
            setSelectedActivity(null);
            fetchActivities();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete activity');
        } finally {
            setSubmitting(false);
        }
    };

    const openEditDialog = (activity) => {
        setSelectedActivity(activity);
        setFormData({ name: activity.name, category: activity.category });
        setShowEditDialog(true);
    };

    const openDeleteDialog = (activity) => {
        setSelectedActivity(activity);
        setShowDeleteDialog(true);
    };

    const getCategoryColor = (category) => {
        return CATEGORIES.find(c => c.value === category)?.color || 'bg-gray-500';
    };

    // Group activities by category
    const groupedActivities = activities.reduce((acc, activity) => {
        const category = activity.category || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(activity);
        return acc;
    }, {});

    if (!isNetworkManager()) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="max-w-md w-full">
                    <CardContent className="p-6 text-center">
                        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
                        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground mb-4">
                            Only Network Managers can access this page.
                        </p>
                        <Button onClick={() => navigate('/portal')}>
                            Return to Hub
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background" data-testid="manage-activities-page">
            {/* Header */}
            <header className="bg-primary text-white sticky top-0 z-30">
                <div className="flex items-center justify-between h-14 px-4">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/dashboard')}
                            className="text-white hover:bg-white/20"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <h1 className="text-lg font-semibold">Manage Clock-on Activities</h1>
                    </div>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                            setFormData({ name: '', category: 'Operations' });
                            setShowAddDialog(true);
                        }}
                        data-testid="add-activity-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                    </Button>
                </div>
            </header>

            <div className="p-4 space-y-6">
                {/* Info Card */}
                <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <Settings className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                                <p className="font-medium text-blue-900 dark:text-blue-100">
                                    Clock-on Activity Management
                                </p>
                                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                                    These activities appear in the clock-on popup when members start their shift. 
                                    Default activities cannot be deleted but can be edited.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {loading ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-24 bg-muted rounded-lg animate-pulse"></div>
                        ))}
                    </div>
                ) : (
                    /* Activity Categories */
                    CATEGORIES.map(({ value: category, label, color }) => {
                        const categoryActivities = groupedActivities[category] || [];
                        if (categoryActivities.length === 0) return null;

                        return (
                            <Card key={category}>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <div className={`w-3 h-3 rounded-sm ${color}`}></div>
                                        {label}
                                        <Badge variant="secondary" className="ml-auto">
                                            {categoryActivities.length}
                                        </Badge>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    {categoryActivities.map((activity) => (
                                        <div
                                            key={activity.id}
                                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                                            data-testid={`activity-${activity.id}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <Clock className="w-4 h-4 text-muted-foreground" />
                                                <span className="font-medium">{activity.name}</span>
                                                {activity.is_default && (
                                                    <Badge variant="outline" className="text-xs">
                                                        Default
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEditDialog(activity)}
                                                    data-testid={`edit-${activity.id}`}
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                                {!activity.is_default && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => openDeleteDialog(activity)}
                                                        className="text-destructive hover:text-destructive"
                                                        data-testid={`delete-${activity.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </CardContent>
                            </Card>
                        );
                    })
                )}
            </div>

            {/* Add Dialog */}
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Clock-on Activity</DialogTitle>
                        <DialogDescription>
                            Create a new activity that members can select when clocking on.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Activity Name</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., Community Outreach"
                                data-testid="activity-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Category</Label>
                            <Select
                                value={formData.category}
                                onValueChange={(value) => setFormData({ ...formData, category: value })}
                            >
                                <SelectTrigger data-testid="activity-category-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((cat) => (
                                        <SelectItem key={cat.value} value={cat.value}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${cat.color}`}></div>
                                                {cat.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleAdd} disabled={submitting} data-testid="save-activity-btn">
                            <Save className="w-4 h-4 mr-1" />
                            Create Activity
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Activity</DialogTitle>
                        <DialogDescription>
                            Update the activity details.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Activity Name</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                data-testid="edit-activity-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Category</Label>
                            <Select
                                value={formData.category}
                                onValueChange={(value) => setFormData({ ...formData, category: value })}
                            >
                                <SelectTrigger data-testid="edit-activity-category-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {CATEGORIES.map((cat) => (
                                        <SelectItem key={cat.value} value={cat.value}>
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full ${cat.color}`}></div>
                                                {cat.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleEdit} disabled={submitting} data-testid="update-activity-btn">
                            <Save className="w-4 h-4 mr-1" />
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Activity?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{selectedActivity?.name}"? 
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-btn"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
